import { uiText, useUiLanguage } from '@/i18n/interface';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAdminPaymentManager } from '@/hooks/useAdminPaymentManager';
import PaymentCategoriesTab from './PaymentCategoriesTab';
import PaymentInstructionsTab from './PaymentInstructionsTab';
import PaymentMethodsManager from './PaymentMethodsManager';
import StudentPaymentsTab from './StudentPaymentsTab';
import { PaymentCategory } from '@/types/payment';

const AdminPaymentManager = ({ initialTab = 'categories' }: { initialTab?: string }) => {
  useUiLanguage();
  const { t } = useTranslation('payments');
  const {
    categories,
    students,
    studentPayments,
    isLoading,
    selectedStudent,
    paymentDetails,
    setSelectedStudent,
    setPaymentDetails,
    updateCategory,
    updatePaymentDates,
    deletePayment,
    sendPaymentInstructions,
    fetchStudentPayments,
    fetchStudents
  } = useAdminPaymentManager();

  // Local state to track all categories including newly added ones
  const [allCategories, setAllCategories] = useState<PaymentCategory[]>(categories);

  // Update allCategories when categories from hook change
  React.useEffect(() => {
    setAllCategories(categories);
  }, [categories]);

  const handleCategoriesChange = (updatedCategories: PaymentCategory[]) => {
    setAllCategories(updatedCategories);
  };

  return (
    <div className="space-y-4 md:space-y-6">
      <Tabs defaultValue={initialTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 h-auto sm:h-10 gap-1 p-1">
          <TabsTrigger value="categories" className="text-xs sm:text-sm p-2 sm:p-3">
            <span className="hidden sm:inline">{t("payments:categories")}</span>
            <span className="sm:hidden">{t("payments:categories")}</span>
          </TabsTrigger>
          <TabsTrigger value="students" className="text-xs sm:text-sm p-2 sm:p-3">
            <span className="hidden sm:inline">{t("payments:history")}</span>
            <span className="sm:hidden">{t("payments:history")}</span>
          </TabsTrigger>
          <TabsTrigger value="instructions" className="text-xs sm:text-sm p-2 sm:p-3">
            <span className="hidden sm:inline">{t("payments:instructions")}</span>
            <span className="sm:hidden">{t("payments:instructions")}</span>
          </TabsTrigger>
          <TabsTrigger value="methods" className="text-xs sm:text-sm p-2 sm:p-3">
            <span className="hidden sm:inline">{uiText("Payment Methods")}</span>
            <span className="sm:hidden">{uiText("Methods")}</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="categories" className="space-y-2 sm:space-y-4 mt-4">
          <PaymentCategoriesTab
            categories={categories}
            onUpdateCategory={updateCategory}
            onCategoriesChange={handleCategoriesChange}
          />
        </TabsContent>

        <TabsContent value="students" className="space-y-2 sm:space-y-4 mt-4">
          <StudentPaymentsTab
            studentPayments={studentPayments}
            students={students}
            onRefresh={() => {
              fetchStudentPayments();
              fetchStudents();
            }}
            onEditPayment={(payment) => console.log('Edit payment:', payment)}
            onDeletePayment={(paymentId) => console.log('Delete payment:', paymentId)}
          />
        </TabsContent>

        <TabsContent value="instructions" className="space-y-2 sm:space-y-4 mt-4">
          <PaymentInstructionsTab
            students={students}
            categories={allCategories}
            selectedStudent={selectedStudent}
            setSelectedStudent={setSelectedStudent}
            paymentDetails={paymentDetails}
            setPaymentDetails={setPaymentDetails}
            isLoading={isLoading}
            onSendPaymentInstructions={() => {
              if (selectedStudent) sendPaymentInstructions();
            }}
          />
        </TabsContent>

        <TabsContent value="methods" className="space-y-2 sm:space-y-4 mt-4">
          <PaymentMethodsManager />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminPaymentManager;
